import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "../styles/ServiceAmount.css";
import OrderConfirmationPopup from "../serviceOverview/OrderConfirmationPopup";
import AutoDebitUpdate from "../services/AutoDebitUpdate";
import Swal from "sweetalert2";
import { termsData as termsContent } from "./termsData";

const TermsConditionsPopup = ({ onClose, selectedPlan, totalAmount, serviceId, serviceName, autoDebitStatus }) => {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [showOrderConfirmation, setShowOrderConfirmation] = useState(false);
  const [orderResponse, setOrderResponse] = useState(null); // 🔹 store backend response
  const [isProcessing, setIsProcessing] = useState(false); // 🔹 track order processing state
  const termsRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const element = termsRef.current;
      if (!element) return;

      const scrollableHeight = element.scrollHeight - element.clientHeight;
      // Unlock agreement after user has read at least 50% of terms content.
      const scrolledRatio = scrollableHeight <= 0 ? 1 : element.scrollTop / scrollableHeight;
      if (scrolledRatio >= 0.5) {
        setIsScrolled(true);
      }
    };

    const ref = termsRef.current;
    if (ref) {
      // If there is little/no scrollable content, allow checkbox immediately.
      const scrollableHeight = ref.scrollHeight - ref.clientHeight;
      if (scrollableHeight <= 0) {
        setIsScrolled(true);
      }
      ref.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (ref) {
        ref.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  const handleAcceptClick = async () => {
    // 🔹 Prevent multiple clicks
    if (isProcessing) {
      return;
    }

    setIsProcessing(true); // 🔹 Set processing state

    try {
      const response = await AutoDebitUpdate.generateOrder(
        selectedPlan,
        totalAmount,
        serviceId,
        serviceName,
        autoDebitStatus  
      );

      console.log("✅Order generated:", response);
      
      setOrderResponse(response); // save API response
      setShowOrderConfirmation(true);
    } catch (error) {
      console.error("Error generating order:", error);
      setShowOrderConfirmation(false);
      setIsProcessing(false); // 🔹 Reset processing state on error

      // Handle validation error from backend (400 Bad Request)
      if (error.response && error.response.status === 400) {
        let errorMessage = error.response.data?.creationMessage || 
                           error.response.data?.message || 
                           error.response.data?.statusMessage ||
                           "Cannot purchase this plan at the moment.";
        
        // Cooldown should be shown only when backend explicitly returns cooldown semantics.
        const isCooldownError = errorMessage.toLowerCase().includes("another plan only after") ||
                               errorMessage.toLowerCase().includes("please wait approximately");
        // Check if this is an insufficient balance error
        const isInsufficientBalance = errorMessage.toLowerCase().includes("insufficient balance") || 
                                     errorMessage.toLowerCase().includes("balance");

        if (isCooldownError) {
          await Swal.fire({
            icon: "info",
            title: "Please Wait",
            html: `<div style="text-align: left; padding: 10px;">
                     <p style="margin-bottom: 10px; font-size: 14px; line-height: 1.6;">
                       ${errorMessage}
                     </p>
                   </div>`,
            confirmButtonColor: "#1976d2",
            confirmButtonText: "OK",
            allowOutsideClick: false,
            allowEscapeKey: false,
            customClass: {
              popup: 'swal-wide'
            }
          });
        } else if (isInsufficientBalance) {
          await Swal.fire({
            icon: "warning",
            title: "Insufficient Balance",
            html: `<div style="text-align: left; padding: 10px;">
                     <p style="margin-bottom: 10px; font-size: 14px; line-height: 1.6;">
                       ${errorMessage}
                     </p>
                   </div>`,
            confirmButtonColor: "#d33",
            confirmButtonText: "OK",
            allowOutsideClick: false,
            allowEscapeKey: false,
            customClass: {
              popup: 'swal-wide'
            }
          });
        } else {
          // Remove plan IDs from error message, keep only plan names
          // Pattern: "Cannot purchase plan 'PLAN_NAME' (ID: XXX). You currently have an active plan (ID: XXX) for this service..."
          // Replace with: "Cannot purchase plan 'PLAN_NAME'. You currently have an active plan for this service..."
          errorMessage = errorMessage.replace(/\(ID:\s*\d+\)/gi, "");
          // Clean up any extra spaces that might result
          errorMessage = errorMessage.replace(/\s+/g, " ").trim();
          
          await Swal.fire({
            icon: "warning",
            title: "Plan Purchase Restriction",
            html: `<div style="text-align: left; padding: 10px;">
                     <p style="margin-bottom: 10px; font-size: 14px; line-height: 1.6;">
                       ${errorMessage}
                     </p>
                   </div>`,
            confirmButtonColor: "#d33",
            confirmButtonText: "OK",
            allowOutsideClick: false,
            allowEscapeKey: false,
            customClass: {
              popup: 'swal-wide'
            }
          });
        }
      } 
      // Handle unauthorized error (401)
      else if (error.response && error.response.status === 401) {
        await Swal.fire({
          icon: "error",
          title: "Unauthorized",
          text: "You are not authorized to generate Order",
          confirmButtonColor: "#d33",
          confirmButtonText: "OK"
        });
      } 
      // Handle other errors
      else {
        await Swal.fire({
          icon: "error",
          title: "Error",
          text: error.response?.data?.message || "Something went wrong while generating order.",
          confirmButtonColor: "#d33",
          confirmButtonText: "OK"
        });
      }
    } finally {
      setIsProcessing(false); // 🔹 Reset processing state
    }
  };

  const closeOrderConfirmation = () => {
    setShowOrderConfirmation(false);
    onClose();
  };

  const handleCheckboxChange = (event) => {
    setIsCheckboxChecked(event.target.checked);
  };

  const termsData = termsContent;

  return (
    <div className="popup-overlay">
      <div className="popup-content terms-popup">
        <div className="popup-header">
          <h5 className="popup-title">{t("terms_conditions")}</h5>
          <button className="popup-close" onClick={onClose}>X</button>
        </div>
        <div className="popup-body">
          <div
            className="terms-content"
            ref={termsRef}
            style={{
              maxHeight: "400px",
              overflowY: "auto",
              padding: "10px",
              border: "1px solid #ddd",
              borderRadius: "5px",
              backgroundColor: "#f9f9f9",
            }}
          >
            {termsData ? (
              <>
                <h4>{termsData.title}</h4>
                {termsData.sections.map((section, index) => (
                  <div key={index}>
                    <h5>{section.title}</h5>
                    {section.content.map((paragraph, pIndex) => (
                      <p key={pIndex}>{paragraph}</p>
                    ))}
                  </div>
                ))}
              </>
            ) : (
              <p>{t("terms_paragraph")}</p>
            )}
          </div>

          <div className="terms-checkbox">
            <input
              type="checkbox"
              id="terms"
              disabled={!isScrolled}  // 🔹 enable only after scroll
              checked={isCheckboxChecked}
              onChange={handleCheckboxChange}
              tabIndex="-1" 
            />
            <label htmlFor="terms">{t("agree_terms")}</label>
          </div>
        </div>

        <div className="popup-footers">
          <button
            className={`accept-btn ${isCheckboxChecked ? "active" : ""}`}
            disabled={!isCheckboxChecked || isProcessing}
            onClick={handleAcceptClick}
          >
            {isProcessing ? "Processing..." : t("accept")}
          </button>
        </div>
      </div>

      {showOrderConfirmation && orderResponse && (
        <OrderConfirmationPopup
          onClose={closeOrderConfirmation}
          selectedPlan={selectedPlan}
          orderResponse={orderResponse}  // 🔹 pass backend response
        />
      )}
    </div>
  );
};

export default TermsConditionsPopup;
